import React from 'react';
import { Task } from '@shared/types';
import { cn } from '@shared/lib/utils';
import { Badge } from '@shared/components/ui/badge';
import { Avatar, AvatarFallback } from '@shared/components/ui/avatar';
import { CalendarDays, FileText, Link2 } from 'lucide-react';
import { differenceInCalendarDays, parseISO } from 'date-fns';

interface TaskCardProps {
  task: Task;
  onClick: (task: Task) => void;
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>;
  isDragging?: boolean;
}

function DueDateBadge({ dueDate }: { dueDate: string }) {
  const days = differenceInCalendarDays(parseISO(dueDate), new Date());
  const label = days < 0
    ? `Overdue ${Math.abs(days)}d`
    : days === 0
    ? 'Due today'
    : `Due in ${days}d`;
  const isOverdue = days < 0;
  const isSoon = !isOverdue && days <= 2;
  return (
    <Badge
      variant={isOverdue ? 'destructive' : 'secondary'}
      className={cn('gap-1 text-xs font-normal', isSoon && 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400')}
    >
      <CalendarDays className="h-3 w-3" />
      {label}
    </Badge>
  );
}

export function TaskCard({ task, onClick, dragHandleProps, isDragging }: TaskCardProps) {
  const assignees = task.assignees ?? [];
  const linkCount = task.links?.length ?? 0;

  return (
    <div
      {...dragHandleProps}
      onClick={() => onClick(task)}
      className={cn(
        'group bg-card border border-border rounded-xl p-3 cursor-pointer',
        'hover:border-primary/40 hover:shadow-sm transition-all',
        isDragging && 'opacity-50 rotate-1 shadow-lg'
      )}
    >
      <p className="text-sm font-medium leading-snug line-clamp-2 mb-2">{task.title}</p>

      <div className="flex items-center gap-2 flex-wrap">
        {task.due_date && <DueDateBadge dueDate={task.due_date} />}

        {task.note_id && (
          <Badge variant="outline" className="gap-1 text-xs font-normal">
            <FileText className="h-3 w-3" />
            Note
          </Badge>
        )}

        {linkCount > 0 && (
          <Badge variant="outline" className="gap-1 text-xs font-normal">
            <Link2 className="h-3 w-3" />
            {linkCount}
          </Badge>
        )}
      </div>

      {assignees.length > 0 && (
        <div className="flex items-center gap-1 mt-2">
          {assignees.slice(0, 4).map(a => (
            <Avatar key={a.user_id} className="h-5 w-5">
              <AvatarFallback className="text-[10px] bg-primary/10 text-primary">
                {(a.user?.name ?? a.user_id).charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          ))}
          {assignees.length > 4 && (
            <span className="text-xs text-muted-foreground">+{assignees.length - 4}</span>
          )}
        </div>
      )}
    </div>
  );
}
